import type {UseComboboxProps, UseMultipleSelectionProps} from 'downshift';
import {useCombobox, useMultipleSelection} from 'downshift';
import {Check, X} from 'lucide-react';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Input, type InputProps} from '@/components/ui/input';
import {menuItemVariants} from '@/components/ui/menu-item';
import {Popover, PopoverAnchor, PopoverContent} from '@/components/ui/popover';
import {cn} from '@/utils/ui';

import CircularProgress from '../../CircularProgress';
import {Button} from '../button';
import {IconButton} from '../icon-button';

export type Multiple<T> = UseMultipleSelectionProps<T>;

export type ComboboxProps<T> = UseComboboxProps<T> & {
  className?: string;
  renderItem?: (item: T) => React.ReactNode;
  getGroupLabel?: (
    item: T,
    index: number,
    filteredItems: T[]
  ) => React.ReactNode;
  filterItem?: (item: T, inputValue: string) => boolean;
  multiple?: Multiple<T>;
  isLoading?: boolean;
  isItemSelected?: (item: T) => boolean;
  inputProps?: InputProps;
  'aria-invalid'?: boolean | 'true' | 'false' | 'grammar' | 'spelling';
  'aria-describedby'?: string;
  id?: string;
};

const Combobox = <T,>({
  items,
  className,
  renderItem,
  getGroupLabel,
  filterItem,
  itemToString,
  multiple,
  inputValue: controlableInputValue,
  isItemDisabled = () => false,
  isItemSelected,
  isLoading = false,
  inputProps = {},
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
  id,
  ...props
}: ComboboxProps<T>) => {
  const {t} = useTranslation();
  const [inputValue, setInputValue] = useState(controlableInputValue || '');

  const toString = (item: T | null) =>
    itemToString ? itemToString(item) : String(item ?? '');

  const {
    onInputValueChange,
    onSelectedItemChange,
    onStateChange,
    stateReducer: customStateReducer,
    ...comboboxProps
  } = props;
  const selectedItemProp = props.selectedItem;

  useEffect(() => {
    if (multiple) return;

    if (controlableInputValue !== undefined && controlableInputValue !== null) {
      return;
    }

    if (selectedItemProp) {
      setInputValue(toString(selectedItemProp));
    } else {
      setInputValue('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItemProp, multiple, controlableInputValue]);

  const effectiveInputValue = controlableInputValue ?? inputValue;
  const filteredItems = items.filter(item =>
    filterItem
      ? filterItem(item, effectiveInputValue)
      : toString(item).toLowerCase().includes(effectiveInputValue.toLowerCase())
  );

  const {selectedItems, ...multipleProps} = multiple || {};

  const {
    getSelectedItemProps,
    getDropdownProps,
    removeSelectedItem,
    setSelectedItems,
  } = useMultipleSelection<T>({
    selectedItems,
    ...multipleProps,
  });

  const {
    isOpen,
    getMenuProps,
    getInputProps,
    getItemProps,
    highlightedIndex,
    selectedItem,
    reset,
  } = useCombobox<T>({
    items: filteredItems,
    isItemDisabled,
    itemToString: toString,
    inputValue: controlableInputValue ?? inputValue,
    onInputValueChange: changes => {
      const isSelectionInputChange =
        !multiple &&
        (changes.type === useCombobox.stateChangeTypes.InputKeyDownEnter ||
          changes.type === useCombobox.stateChangeTypes.ItemClick ||
          (changes.type ===
            useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem &&
            selectedItemProp === null));

      if (isSelectionInputChange) return;

      setInputValue(changes.inputValue || '');
      onInputValueChange?.(changes);
    },
    stateReducer: (state, actionAndChanges) => {
      const {changes, type} = actionAndChanges;
      const reducedChanges = (() => {
        switch (type) {
          case useCombobox.stateChangeTypes.InputBlur:
            return {
              ...changes,
              inputValue:
                changes.selectedItem instanceof Object
                  ? toString?.(changes.selectedItem) || changes.inputValue
                  : state.inputValue,
            };
          case useCombobox.stateChangeTypes.InputClick:
            return {
              ...changes,
              isOpen: true,
            };
          case useCombobox.stateChangeTypes.InputKeyDownEnter:
          case useCombobox.stateChangeTypes.ItemClick:
            if (multiple) return changes;

            return {
              ...changes,
              inputValue: changes.selectedItem
                ? toString(changes.selectedItem)
                : changes.inputValue,
            };
          case useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem:
            if (multiple || selectedItemProp !== null) return changes;

            return {
              ...changes,
              inputValue: state.inputValue,
            };
          default:
            return changes;
        }
      })();

      return customStateReducer
        ? customStateReducer(state, {
            ...actionAndChanges,
            changes: reducedChanges,
          })
        : reducedChanges;
    },
    onSelectedItemChange: changes => {
      if (!multiple && changes.selectedItem) {
        setInputValue(toString(changes.selectedItem));
      }

      onSelectedItemChange?.(changes);
    },
    onStateChange: changes => {
      if (multiple) {
        const {
          inputValue: newInputValue,
          type,
          selectedItem: newSelectedItem,
        } = changes;

        switch (type) {
          case useCombobox.stateChangeTypes.InputKeyDownEnter:
          case useCombobox.stateChangeTypes.ItemClick:
          case useCombobox.stateChangeTypes.InputBlur:
            if (newSelectedItem) {
              setSelectedItems?.([...(selectedItems || []), newSelectedItem]);
              setInputValue('');
            }
            break;

          case useCombobox.stateChangeTypes.InputChange:
            setInputValue(newInputValue || '');

            break;
          default:
            break;
        }
      }
      onStateChange?.(changes);
    },
    selectedItem: multiple ? null : undefined,
    ...comboboxProps,
  });

  const handleReset = () => {
    reset();
  };

  return (
    <div className={cn(className)}>
      {multiple && selectedItems?.length !== 0 && (
        <div className="flex flex-row flex-wrap items-center gap-2 pb-3">
          {selectedItems?.map((item, index) => (
            <div
              {...getSelectedItemProps({index, selectedItem: item})}
              className="flex items-center gap-1 rounded border border-marian-800 px-2 text-sm"
              key={toString?.(item) || index}
            >
              {renderItem ? renderItem(item) : toString?.(item)}
              <IconButton
                disabled={inputProps.disabled}
                type="button"
                variant="link-accent"
                size="xsmall"
                onClick={e => {
                  e.stopPropagation();
                  removeSelectedItem(item);
                }}
              >
                <span className="sr-only">
                  {t('Delete')}{' '}
                  {renderItem ? renderItem(item) : toString?.(item)}
                </span>
                <X />
              </IconButton>
            </div>
          ))}
          <Button
            variant="link-accent"
            size="text"
            onClick={() => {
              setSelectedItems?.([]);
            }}
            type="button"
            className="text-sm"
            disabled={inputProps.disabled}
          >
            {t('Clear all')}
          </Button>
        </div>
      )}

      <Popover open={isOpen}>
        <PopoverAnchor asChild>
          <div className="relative">
            <Input
              {...getInputProps(
                getDropdownProps({
                  preventKeyAction: isOpen,
                })
              )}
              id={id}
              aria-invalid={ariaInvalid}
              aria-describedby={ariaDescribedBy}
              {...inputProps}
            />
            {isLoading && (
              <div className="absolute top-[50%] right-2 flex -translate-y-[50%] justify-center">
                <CircularProgress size={20} />
              </div>
            )}
            {effectiveInputValue && !isLoading && (
              <IconButton
                disabled={inputProps.disabled}
                className="absolute top-[50%] right-2 -translate-y-[50%]"
                type="button"
                variant="link-accent"
                size="xsmall"
                onClick={handleReset}
                tabIndex={-1}
              >
                <span className="sr-only">{t('Delete')} </span>
                <X />
              </IconButton>
            )}
          </div>
        </PopoverAnchor>
        <PopoverContent
          forceMount
          asChild
          avoidCollisions
          onOpenAutoFocus={e => {
            e.preventDefault();
          }}
          className={cn('w-(--radix-popper-anchor-width) p-2')}
        >
          <ul
            {...getMenuProps({}, {suppressRefError: true})}
            className="app-scrollbar max-h-[200px] overflow-auto"
          >
            {filteredItems.map((item, index) => {
              const selected = isItemSelected?.(item) || selectedItem === item;
              const groupLabel = getGroupLabel?.(item, index, filteredItems);

              return (
                <li key={index} role="presentation">
                  {groupLabel && (
                    <div className="px-1.5 pt-2 pb-1 text-xs font-semibold text-muted-foreground first:pt-0">
                      {groupLabel}
                    </div>
                  )}
                  <div
                    {...getItemProps({
                      item,
                      index,
                    })}
                    className={cn(
                      menuItemVariants(),
                      'cursor-pointer',
                      selected &&
                        'bg-secondary-light text-secondary-foreground hover:bg-secondary-light',
                      highlightedIndex === index &&
                        !selected &&
                        'bg-neutral-light text-secondary-foreground',
                      isItemDisabled?.(item, index) &&
                        'cursor-not-allowed opacity-50'
                    )}
                  >
                    <div className="min-w-0 grow">
                      {renderItem
                        ? renderItem(item)
                        : toString?.(item) || String(item)}
                    </div>
                    {selected && <Check className="mr-4 size-4" />}
                  </div>
                </li>
              );
            })}

            {filteredItems.length === 0 && (
              <li className="p-1.5 text-sm">{t('No results found')}</li>
            )}
          </ul>
        </PopoverContent>
      </Popover>
    </div>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export {Combobox, useCombobox};
